import React, { useState, useEffect, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme, Snackbar } from 'react-native-paper';
import LinearGradient from 'react-native-linear-gradient';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAuth } from '../context/AuthContext';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import TruckService from '../services/truckService';
import { Truck } from '../types/Truck';
import { ErrorLogger } from '../utils/errorLogger';
import FloatingActionButton from '../components/FloatingActionButton';
import AddTruckBottomSheet from '../components/AddTruckBottomSheet';
import TruckList from '../components/TruckList';
import ServiceService from '../services/serviceService';
import { ServiceSummaryByTruck } from '../types/Service';
import { RootStackParamList } from '../../App';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';

type FilterStatus = 'all' | 'active' | 'inactive';

interface TrucksScreenProps {
  onNavigateHome?: () => void;
}

const TrucksScreen: React.FC<TrucksScreenProps> = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();

  const [showTruckModal, setShowTruckModal] = useState(false);
  const [showDeleteSuccessSnackbar, setShowDeleteSuccessSnackbar] = useState(false);
  const [showCreateSuccessSnackbar, setShowCreateSuccessSnackbar] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [serviceSummary, setServiceSummary] = useState<ServiceSummaryByTruck>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');

  const { data: trucks } = useFirestoreSubscription(
    (onUpdate, onError) => {
      if (!user?.uid) { onUpdate([]); return () => {}; }
      return TruckService.subscribeToAllTrucks(onUpdate, onError);
    },
    [] as Truck[],
  );

  const loadServiceSummary = useCallback(async () => {
    if (!user?.uid) { setServiceSummary({}); setLoadingSummary(false); return; }
    try {
      setLoadingSummary(true);
      const summary = await ServiceService.getOverdueSummaryByTruck();
      setServiceSummary(summary);
    } catch (error) {
      ErrorLogger.logError(error as any, 'TrucksScreen.loadServiceSummary');
      setServiceSummary({});
    } finally {
      setLoadingSummary(false);
    }
  }, [user?.uid]);

  useEffect(() => { loadServiceSummary(); }, [loadServiceSummary]);

  const totalTrucks = trucks.length;
  const activeTrucks = trucks.filter(t => t.isActive).length;
  const inactiveTrucks = totalTrucks - activeTrucks;

  const overdueCount = Object.values(serviceSummary).reduce(
    (sum, s) => sum + (s.overdue ?? 0), 0,
  );

  const filterOptions: { key: FilterStatus; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' },
  ];

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={[styles.headerTitle, { color: theme.colors.onBackground }]}>
            Fleet Management
          </Text>
          <Text style={[styles.headerSubtitle, { color: theme.colors.onSurfaceVariant }]}>
            {loadingSummary
              ? 'Loading fleet data...'
              : `Real-time status for ${activeTrucks} active unit${activeTrucks !== 1 ? 's' : ''}`}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.refreshButton, { backgroundColor: theme.colors.surfaceVariant }]}
          onPress={loadServiceSummary}
        >
          <MaterialCommunityIcons name="refresh" size={20} color={theme.colors.onSurfaceVariant} />
        </TouchableOpacity>
      </View>

      {/* Hero card */}
      <View style={styles.cardWrapper}>
        <LinearGradient
          colors={['#063669', '#274e82']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.heroCard}
        >
          <View style={styles.heroRow}>
            <View style={styles.heroLeft}>
              <Text style={styles.heroLabel}>FLEET OVERVIEW</Text>
              <Text style={styles.heroAmount}>{loadingSummary ? '—' : totalTrucks}</Text>
              <Text style={styles.heroSub}>truck{totalTrucks !== 1 ? 's' : ''} in fleet</Text>
            </View>
            <View style={styles.heroBadge}>
              <Text style={styles.heroBadgeCount}>{loadingSummary ? '—' : activeTrucks}</Text>
              <Text style={styles.heroBadgeLabel}>active</Text>
            </View>
          </View>

          <View style={styles.tilesRow}>
            <View style={styles.tile}>
              <Text style={styles.tileLabel}>INACTIVE</Text>
              <Text style={styles.tileCount}>{inactiveTrucks}</Text>
              <Text style={styles.tileSub}>unit{inactiveTrucks !== 1 ? 's' : ''}</Text>
            </View>
            <View style={[styles.tile, overdueCount > 0 && styles.overdueHighlight]}>
              <Text style={styles.tileLabel}>SERVICE OVERDUE</Text>
              <Text style={[styles.tileCount, overdueCount > 0 && styles.overdueText]}>
                {overdueCount}
              </Text>
              <Text style={styles.tileSub}>unit{overdueCount !== 1 ? 's' : ''}</Text>
            </View>
          </View>
        </LinearGradient>
      </View>

      {/* Search bar */}
      <View style={[styles.searchBar, { backgroundColor: theme.colors.surfaceVariant }]}>
        <MaterialCommunityIcons name="magnify" size={20} color={theme.colors.onSurfaceVariant} />
        <TextInput
          style={[styles.searchInput, { color: theme.colors.onSurface }]}
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search by plate or model..."
          placeholderTextColor={theme.colors.onSurfaceVariant}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close-circle" size={18} color={theme.colors.onSurfaceVariant} />
          </TouchableOpacity>
        )}
      </View>

      {/* Filter pills */}
      <View style={styles.pillsRow}>
        {filterOptions.map(({ key, label }) => {
          const active = filterStatus === key;
          return (
            <TouchableOpacity
              key={key}
              style={[
                styles.pill,
                active
                  ? { backgroundColor: theme.colors.primary }
                  : { backgroundColor: theme.colors.surfaceVariant },
              ]}
              onPress={() => setFilterStatus(key)}
            >
              <Text
                style={[
                  styles.pillText,
                  { color: active ? theme.colors.onPrimary : theme.colors.onSurfaceVariant },
                ]}
              >
                {label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Truck list */}
      <TruckList
        searchQuery={searchQuery}
        filterStatus={filterStatus}
        onDeleteSuccess={handleDeleteSuccess}
        onTruckPress={handleTruckPress}
        serviceSummary={serviceSummary}
      />

      <View style={styles.fabContainer}>
        <FloatingActionButton onPress={() => setShowTruckModal(true)} />
      </View>

      <AddTruckBottomSheet
        visible={showTruckModal}
        onTruckCreated={() => {}}
        onClose={() => setShowTruckModal(false)}
        onSuccess={() => setShowCreateSuccessSnackbar(true)}
      />

      <Snackbar
        visible={showDeleteSuccessSnackbar}
        onDismiss={() => setShowDeleteSuccessSnackbar(false)}
        duration={3000}
        action={{ label: 'OK', onPress: () => setShowDeleteSuccessSnackbar(false) }}
      >
        Truck deleted successfully!
      </Snackbar>
      <Snackbar
        visible={showCreateSuccessSnackbar}
        onDismiss={() => setShowCreateSuccessSnackbar(false)}
        duration={3000}
        action={{ label: 'OK', onPress: () => setShowCreateSuccessSnackbar(false) }}
      >
        Truck added successfully!
      </Snackbar>
    </SafeAreaView>
  );

  function handleDeleteSuccess() {
    setShowDeleteSuccessSnackbar(true);
  }

  function handleTruckPress(truck: Truck) {
    navigation.navigate('TruckDetail', { truck });
  }
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
  },
  headerLeft: { flex: 1, marginRight: 12 },
  headerTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 22,
  },
  headerSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    marginTop: 3,
    lineHeight: 18,
  },
  refreshButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  cardWrapper: {
    paddingHorizontal: 20,
    marginBottom: 14,
  },
  heroCard: {
    borderRadius: 6,
    padding: 20,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.06,
    shadowRadius: 40,
    elevation: 4,
  },
  heroRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  heroLeft: {
    flex: 1,
  },
  heroLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  heroAmount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 34,
    color: 'white',
    lineHeight: 40,
  },
  heroSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.7)',
    marginTop: 2,
  },
  heroBadge: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 6,
    alignItems: 'center',
  },
  heroBadgeCount: {
    fontFamily: 'Manrope-Bold',
    fontSize: 18,
    color: 'white',
    lineHeight: 22,
  },
  heroBadgeLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.8)',
  },
  tilesRow: {
    flexDirection: 'row',
    gap: 10,
  },
  tile: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 6,
    padding: 14,
  },
  overdueHighlight: {
    borderWidth: 1,
    borderColor: 'rgba(255,180,100,0.4)',
  },
  tileLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.75)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  tileCount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 20,
    color: 'white',
    lineHeight: 24,
  },
  tileSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
    marginTop: 2,
  },
  overdueText: {
    color: '#ffb59a',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    padding: 0,
  },
  pillsRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 12,
    gap: 8,
  },
  pill: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 999,
  },
  pillText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    lineHeight: 18,
  },
  fabContainer: {
    position: 'absolute',
    bottom: 16,
    right: 16,
  },
});

export default TrucksScreen;
